import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import { Reveal, FadeIn, MaskedLines } from "@/components/Motion";
import { getArticle, relatedArticles, readingMinutes } from "@/data/articles";
import NotFound from "@/pages/NotFound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ArticlePage() {
  const { slug } = useParams();
  const [dynamicArticle, setDynamicArticle] = useState(null);
  const [checked, setChecked] = useState(getArticle(slug) !== undefined);

  useEffect(() => {
    let alive = true;
    if (getArticle(slug)) {
      setChecked(true);
      return undefined;
    }
    setDynamicArticle(null);
    setChecked(false);
    axios
      .get(`${API}/articles/${slug}`)
      .then((res) => {
        if (alive) setDynamicArticle(res.data?.article || null);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setChecked(true);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  const article = getArticle(slug) || dynamicArticle;

  if (!article) {
    if (!checked) {
      return <div className="min-h-[60vh] bg-navy-deep" aria-hidden="true" />;
    }
    return <NotFound />;
  }

  const related = relatedArticles(article);

  return (
    <>
      <Seo
        title={`${article.title} | Steven Philips & Associates Limited`}
        description={article.excerpt.slice(0, 158)}
      />
      <article data-testid="article-page">
        <section className="relative overflow-hidden bg-navy-deep pb-16 pt-40 lg:pb-24 lg:pt-52">
          <div className="grain absolute inset-0" aria-hidden="true" />
          <div
            aria-hidden="true"
            className="absolute -right-24 top-0 h-full w-1/2 bg-[radial-gradient(ellipse_at_top_right,rgba(196,164,106,0.1),transparent_60%)]"
          />
          <div className="container-x relative max-w-3xl">
            <FadeIn y={16}>
              <Link
                to="/insights"
                data-testid="article-back-link"
                className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-gold"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                All Insights
              </Link>
            </FadeIn>
            <p className="eyebrow mt-10 flex flex-wrap items-center gap-x-4 gap-y-2">
              {article.category}
              <span aria-hidden="true" className="hidden h-1 w-1 rotate-45 bg-gold sm:inline-block" />
              <span className="font-normal normal-case tracking-normal text-white/50">
                {article.date} · {readingMinutes(article)} min read
              </span>
            </p>
            <h1 className="mt-6 font-serif text-3xl font-medium leading-[1.14] text-white sm:text-4xl lg:text-5xl">
              <MaskedLines delay={0.35} lines={[<span key="t">{article.title}</span>]} />
            </h1>
          </div>
        </section>

        <section className="bg-white py-16 lg:py-24">
          <div className="container-x max-w-3xl">
            <Reveal>
              <img
                src={article.image}
                alt={article.alt}
                className="aspect-[21/9] w-full object-cover"
                loading="lazy"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-12 font-serif text-xl leading-relaxed text-navy sm:text-2xl">
                {article.lede}
              </p>
              <span aria-hidden="true" className="mt-10 block h-px w-20 bg-gold" />
            </Reveal>
            {article.sections.map((s, i) => (
              <Reveal key={s.heading} delay={0.05}>
                <section className="mt-12">
                  <h2 className="font-serif text-2xl font-semibold text-navy sm:text-[1.7rem]">
                    {s.heading}
                  </h2>
                  {s.paragraphs.map((p, j) => (
                    <p key={j} className="mt-5 text-base leading-[1.85] text-charcoal/75 sm:text-[17px]">
                      {p}
                    </p>
                  ))}
                </section>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="border-t border-navy/10 bg-mist py-16 lg:py-24" data-testid="further-reading">
          <div className="container-x">
            <Reveal>
              <p className="eyebrow">Further Reading</p>
              <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">
                Continue the Conversation
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              {related.map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.08}>
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`further-reading-${a.slug}`}
                    className="group flex h-full flex-col border border-navy/10 bg-white p-8 transition-all duration-300 hover:border-gold/50 hover:shadow-[0_18px_40px_-24px_rgba(16,36,58,0.35)]"
                  >
                    <span className="eyebrow">{a.category}</span>
                    <span className="mt-4 flex-1 font-serif text-xl font-semibold leading-snug text-navy">
                      {a.title}
                    </span>
                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-navy">
                      Read
                      <ArrowRight
                        aria-hidden="true"
                        className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-navy-deep py-20 lg:py-28">
          <div className="grain absolute inset-0" aria-hidden="true" />
          <div className="container-x relative max-w-2xl text-center">
            <Reveal>
              <h2 className="font-serif text-3xl font-semibold leading-snug text-white sm:text-4xl">
                Considering these questions for your organization?
              </h2>
              <p className="mt-6 text-base leading-relaxed text-white/65">
                Our advisory team helps leadership teams turn perspective into
                decisions. Start the conversation today.
              </p>
              <Link to="/contact" data-testid="article-cta" className="btn-gold mt-9">
                Schedule a Consultation
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </section>
      </article>
    </>
  );
}

export default ArticlePage;
